<template>
  <div class="partner-dashboard pa-10">

    <!-- Header -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7">
      <div>
        <div class="dash-page-title">Partner Dashboard</div>
        <div class="dash-page-sub mt-1">Manage Interns &amp; Track Performance</div>
      </div>
      <v-btn
        color="#00A63E"
        variant="flat"
        class="text-white"
        prepend-icon="mdi-plus"
        rounded="lg"
        size="large"
        to="/organization/request-intern"
      >
        Request New Interns
      </v-btn>
    </div>

    <!-- Stat Cards -->
    <v-row class="mb-5" style="gap: 0">
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--green rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--green rounded-lg pa-2">
              <v-icon size="22" color="#059669">mdi-account-multiple-outline</v-icon>
            </div>
            <v-icon size="18" color="#059669">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--green">{{ stats.active_interns }}</div>
          <div class="stat-label stat-label--green">Active Interns</div>
          <div class="stat-sub">Currently training</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--blue rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--blue rounded-lg pa-2">
              <v-icon size="22" color="#2563eb">mdi-clock-outline</v-icon>
            </div>
            <v-icon size="18" color="#2563eb">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--blue">{{ stats.total_hours.toLocaleString() }}</div>
          <div class="stat-label stat-label--blue">Total Hours Logged</div>
          <div class="stat-sub">Across all interns</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--amber rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--amber rounded-lg pa-2">
              <v-icon size="22" color="#d97706">mdi-clipboard-check-outline</v-icon>
            </div>
            <v-icon size="18" color="#d97706">mdi-alert-outline</v-icon>
          </div>
          <div class="stat-number stat-number--amber">{{ stats.pending_evaluations }}</div>
          <div class="stat-label stat-label--amber">Pending Evaluations</div>
          <div class="stat-sub">Awaiting your review</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--purple rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--purple rounded-lg pa-2">
              <v-icon size="22" color="#7c3aed">mdi-briefcase-outline</v-icon>
            </div>
            <v-icon size="18" color="#7c3aed">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--purple">{{ stats.open_positions }}</div>
          <div class="stat-label stat-label--purple">Open Positions</div>
          <div class="stat-sub">Currently hiring</div>
        </div>
      </v-col>
    </v-row>

    <!-- Charts Row 1 -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Intern Status Overview</div>
            <v-icon size="20" color="#94a3b8">mdi-account-group-outline</v-icon>
          </div>
          <div class="card-sub mb-4">Current status of assigned interns</div>
          <div class="chart-wrap chart-wrap--doughnut">
            <canvas ref="statusChart"></canvas>
          </div>
          <div class="d-flex justify-center ga-5 mt-4 flex-wrap">
            <div class="d-flex align-center ga-1">
              <div class="legend-dot" style="background: #059669"></div>
              <span class="legend-label">Active</span>
            </div>
            <div class="d-flex align-center ga-1">
              <div class="legend-dot" style="background: #2563eb"></div>
              <span class="legend-label">Completed</span>
            </div>
            <div class="d-flex align-center ga-1">
              <div class="legend-dot" style="background: #f59e0b"></div>
              <span class="legend-label">On Leave</span>
            </div>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Weekly Attendance Summary</div>
            <v-icon size="20" color="#94a3b8">mdi-calendar-outline</v-icon>
          </div>
          <div class="card-sub mb-4">Attendance tracking for the past 4 weeks</div>
          <div class="chart-wrap">
            <canvas ref="attendanceChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Charts Row 2 -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Intern Performance Ratings</div>
            <v-icon size="20" color="#94a3b8">mdi-account-star-outline</v-icon>
          </div>
          <div class="card-sub mb-4">Average performance scores</div>
          <div class="chart-wrap">
            <canvas ref="performanceChart"></canvas>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6" style="height: 100%">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Skills Distribution</div>
            <v-icon size="20" color="#94a3b8">mdi-trending-up</v-icon>
          </div>
          <div class="card-sub mb-4">Key skills of current interns</div>
          <div class="chart-wrap">
            <canvas ref="skillsChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Intern Hours Progress -->
    <div class="dash-card rounded-xl pa-6 mb-5">
      <div class="d-flex justify-space-between align-center mb-5">
        <div class="card-title">Intern Hours Progress</div>
        <a class="view-all-link" href="/organization/manage-intern">View All →</a>
      </div>
      <div v-if="!internProgress.length" class="text-center py-6" style="color: #9ca3af; font-size: 13px">
        No intern progress data available.
      </div>
      <div
        v-for="(intern, idx) in internProgress"
        :key="idx"
        :class="idx < internProgress.length - 1 ? 'mb-5' : ''"
      >
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center ga-3">
            <div class="intern-avatar rounded-lg d-flex align-center justify-center flex-shrink-0">
              <v-icon size="18" color="#6b7280">mdi-account-outline</v-icon>
            </div>
            <div>
              <div style="font-size: 13.5px; font-weight: 700; color: #111827">{{ intern.name }}</div>
              <div style="font-size: 12px; color: #9ca3af">
                {{ intern.logged }} / {{ intern.required }} hours ({{ intern.pct }}%)
              </div>
            </div>
          </div>
          <v-chip
            size="x-small"
            :color="intern.pct >= 100 ? 'green' : 'orange'"
            variant="tonal"
          >{{ intern.pct >= 100 ? 'Completed' : 'In Progress' }}</v-chip>
        </div>
        <v-progress-linear
          :model-value="intern.pct"
          :color="intern.pct >= 100 ? '#059669' : '#2563eb'"
          height="6"
          rounded
          bg-color="#f3f4f6"
        />
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="dash-card rounded-xl pa-6 mb-5">
      <div class="d-flex justify-space-between align-center mb-5">
        <div class="card-title">Quick Actions</div>
        <a class="view-all-link">View All →</a>
      </div>
      <v-row>
        <v-col cols="12" sm="4">
          <div class="action-card rounded-xl pa-5" @click="$router.push('/organization/intern-evaluations')">
            <div class="d-flex justify-space-between align-start mb-3">
              <v-icon size="26" color="#d97706">mdi-clipboard-check-outline</v-icon>
              <v-icon size="18" color="#d1d5db">mdi-arrow-right</v-icon>
            </div>
            <div class="action-title">Evaluate Interns</div>
            <div class="action-sub">{{ stats.pending_evaluations }} evaluations pending</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="action-card rounded-xl pa-5" @click="$router.push('/organization/attendance-qr')">
            <div class="d-flex justify-space-between align-start mb-3">
              <v-icon size="26" color="#7c3aed">mdi-qrcode</v-icon>
              <v-icon size="18" color="#d1d5db">mdi-arrow-right</v-icon>
            </div>
            <div class="action-title">Generate QR Code</div>
            <div class="action-sub">Create attendance QR</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="action-card rounded-xl pa-5" @click="$router.push('/organization/manage-intern')">
            <div class="d-flex justify-space-between align-start mb-3">
              <v-icon size="26" color="#2563eb">mdi-account-group-outline</v-icon>
            </div>
            <div class="action-title">View All Interns</div>
            <div class="action-sub">Manage intern profiles</div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Recent Activity -->
    <div class="dash-card rounded-xl pa-6">
      <div class="d-flex justify-space-between align-center mb-5">
        <div>
          <div class="card-title">Recent Activity</div>
          <div class="card-sub">Latest system events</div>
        </div>
        <router-link :to="`/${currentUserPath}/activity-log`" class="activity-view-all-link">View All →</router-link>
      </div>
      <div v-if="activityLoading" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="#10b981" size="32" />
      </div>
      <div v-else-if="!recentActivity.length" class="text-center py-6" style="color: #9ca3af; font-size: 13px">
        No recent activity
      </div>
      <div v-else>
        <div
          v-for="(item, idx) in recentActivity"
          :key="idx"
          class="activity-item rounded-xl pa-4"
          :class="idx < recentActivity.length - 1 ? 'mb-3' : ''"
          :style="getActivityBadgeStyle(item.action)"
        >
          <div class="d-flex align-start ga-3">
            <div class="activity-icon-box rounded-lg pa-2 flex-shrink-0" :style="{ background: getActivityConfig(item.action).iconBg }">
              <v-icon :color="getActivityColor(item.action)" size="18">{{ getActivityIcon(item.action) }}</v-icon>
            </div>
            <div class="flex-grow-1">
              <div class="d-flex justify-space-between align-start">
                <div class="activity-title">{{ item.description || item.action }}</div>
                <v-chip size="x-small" :color="getActivityColor(item.action)" variant="tonal" class="ml-2 flex-shrink-0">{{ getActivityConfig(item.action).label }}</v-chip>
              </div>
              <div class="activity-desc">By {{ item.performed_by_name || 'System' }}</div>
              <div class="activity-time">{{ formatActivityTime(item.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Chart from "chart.js/auto";
export default {
  name: "HTEDashboard",
  data() {
    return {
      charts: {},
      loading: false,
      stats: {
        active_interns: 0,
        total_hours: 0,
        pending_evaluations: 0,
        open_positions: 0,
      },
      internProgress: [
        { name: "Juan Dela Cruz", logged: 320, required: 486, pct: 66 },
        { name: "Maria Clara", logged: 450, required: 486, pct: 93 },
        { name: "Jose Rizal", logged: 486, required: 486, pct: 100 },
      ],
      activities: [
        {
          title: "Maria Clara - Attendance Logged",
          desc: "Clocked in at 8:00 AM",
          time: "2 hours ago",
          icon: "mdi-check-circle-outline",
          iconColor: "#059669",
          iconBg: "#d1fae5",
          bg: "#f0fdf4",
          borderColor: "#a7f3d0",
          timeColor: "#059669",
        },
        {
          title: "Juan Dela Cruz - Evaluation Due",
          desc: "Monthly performance review needed",
          time: "Due in 2 days",
          icon: "mdi-alert-outline",
          iconColor: "#d97706",
          iconBg: "#fef3c7",
          bg: "#fffbeb",
          borderColor: "#fde68a",
          timeColor: "#d97706",
        },
        {
          title: "Jose Rizal - Training Completed",
          desc: "Successfully finished 486-hour requirement",
          time: "1 day ago",
          icon: "mdi-school-outline",
          iconColor: "#2563eb",
          iconBg: "#dbeafe",
          bg: "#eff6ff",
          borderColor: "#bfdbfe",
          timeColor: "#6b7280",
        },
      ],
      chartData: {
        status: { active: 0, completed: 0, on_leave: 0 },
        attendance: [],
        performance: [],
        skills: [],
      },
      recentActivity: [],
      activityLoading: false,
    };
  },
  computed: {
    currentUserPath() {
      return this.$route.path.split('/')[1];
    },
  },
  mounted() {
    this.fetchDashboardData();
    this.fetchRecentActivity();
  },
  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true;
      try {
        const res = await this.axiosCall("/organization/dashboard", "GET");
        if (res && res.data) {
          const d = res.data;
          if (d.stats) this.stats = { ...this.stats, ...d.stats };
          if (d.intern_progress) this.internProgress = d.intern_progress;
          if (d.activities) this.activities = d.activities;
          if (d.chart_data) this.chartData = { ...this.chartData, ...d.chart_data };
        }
      } catch (e) {
        console.error("Failed to load partner dashboard", e);
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.initStatusChart();
          this.initAttendanceChart();
          this.initPerformanceChart();
          this.initSkillsChart();
        });
      }
    },

    initStatusChart() {
      if (this.charts.status) this.charts.status.destroy();
      const { active, completed, on_leave } = this.chartData.status;
      this.charts.status = new Chart(this.$refs.statusChart, {
        type: "doughnut",
        data: {
          labels: ["Active", "Completed", "On Leave"],
          datasets: [
            {
              data: [active || this.stats.active_interns || 3, completed || 1, on_leave || 0],
              backgroundColor: ["#059669", "#2563eb", "#f59e0b"],
              borderWidth: 3,
              borderColor: "#ffffff",
              hoverOffset: 8,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "65%",
          plugins: {
            legend: { display: false },
            tooltip: { bodyFont: { size: 12 } },
          },
        },
      });
    },

    initAttendanceChart() {
      if (this.charts.attendance) this.charts.attendance.destroy();
      const weeks = this.chartData.attendance.length
        ? this.chartData.attendance
        : [
            { week: "Week 1", present: 3, late: 0, absent: 0 },
            { week: "Week 2", present: 2, late: 1, absent: 0 },
            { week: "Week 3", present: 3, late: 0, absent: 0 },
            { week: "Week 4", present: 3, late: 0, absent: 0 },
          ];
      this.charts.attendance = new Chart(this.$refs.attendanceChart, {
        type: "bar",
        data: {
          labels: weeks.map((w) => w.week),
          datasets: [
            {
              label: "Present",
              data: weeks.map((w) => w.present),
              backgroundColor: "#059669",
              borderRadius: 3,
              stack: "a",
            },
            {
              label: "Late",
              data: weeks.map((w) => w.late),
              backgroundColor: "#f59e0b",
              borderRadius: 3,
              stack: "a",
            },
            {
              label: "Absent",
              data: weeks.map((w) => w.absent),
              backgroundColor: "#ef4444",
              borderRadius: 3,
              stack: "a",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                font: { size: 11 },
                color: "#6b7280",
                boxWidth: 12,
                padding: 12,
              },
            },
          },
          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 }, stepSize: 1 },
              border: { display: false },
            },
          },
        },
      });
    },

    initPerformanceChart() {
      if (this.charts.performance) this.charts.performance.destroy();
      const data = this.chartData.performance.length
        ? this.chartData.performance
        : [
            { name: "Juan Dela Cruz", score: 4.2 },
            { name: "Jose Rizal", score: 3.8 },
          ];
      this.charts.performance = new Chart(this.$refs.performanceChart, {
        type: "bar",
        data: {
          labels: data.map((d) => d.name),
          datasets: [
            {
              label: "Rating",
              data: data.map((d) => d.score),
              backgroundColor: "#f59e0b",
              borderRadius: 4,
              barPercentage: 0.45,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              beginAtZero: true,
              max: 5,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              grid: { display: false },
              ticks: { color: "#374151", font: { size: 11 } },
              border: { display: false },
            },
          },
        },
      });
    },

    initSkillsChart() {
      if (this.charts.skills) this.charts.skills.destroy();
      const data = this.chartData.skills.length
        ? this.chartData.skills
        : [
            { skill: "React", count: 2 },
            { skill: "TypeScript", count: 1 },
            { skill: "Node.js", count: 2 },
            { skill: "Python", count: 1 },
            { skill: "UI/UX Design", count: 1 },
          ];
      this.charts.skills = new Chart(this.$refs.skillsChart, {
        type: "bar",
        data: {
          labels: data.map((d) => d.skill),
          datasets: [
            {
              label: "Interns",
              data: data.map((d) => d.count),
              backgroundColor: "#7c3aed",
              borderRadius: 4,
              barPercentage: 0.5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 }, stepSize: 1 },
              border: { display: false },
            },
          },
        },
      });
    },

    async fetchRecentActivity() {
      this.activityLoading = true;
      try {
        const res = await this.axiosCall('/system-logs?page=1&limit=5', 'GET');
        if (res?.data?.data) this.recentActivity = res.data.data;
      } catch (e) {
        console.error('Failed to load recent activity', e);
      } finally {
        this.activityLoading = false;
      }
    },
    getActivityConfig(action) {
      const map = {
        USER_LOGIN:              { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-login',                   label: 'Login'      },
        USER_LOGOUT:             { color: '#6b7280', iconBg: '#f3f4f6', icon: 'mdi-logout',                  label: 'Logout'     },
        BATCH_CREATED:           { color: '#8b5cf6', iconBg: '#ede9fe', icon: 'mdi-layers-plus',             label: 'Batch'      },
        STUDENT_ENDORSED:        { color: '#3b82f6', iconBg: '#dbeafe', icon: 'mdi-account-arrow-right',     label: 'Endorsed'   },
        STUDENT_DEPLOYED:        { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-briefcase-check-outline', label: 'Deployed'   },
        STUDENT_PULLED_OUT:      { color: '#ef4444', iconBg: '#fee2e2', icon: 'mdi-account-minus-outline',   label: 'Pulled Out' },
        STUDENT_REASSIGNED:      { color: '#f59e0b', iconBg: '#fef3c7', icon: 'mdi-account-switch-outline',  label: 'Reassigned' },
        REQUIREMENT_SUBMITTED:   { color: '#3b82f6', iconBg: '#dbeafe', icon: 'mdi-file-upload-outline',     label: 'Submitted'  },
        REQUIREMENT_APPROVED:    { color: '#10b981', iconBg: '#d1fae5', icon: 'mdi-file-check-outline',      label: 'Approved'   },
        REQUIREMENT_DISAPPROVED: { color: '#ef4444', iconBg: '#fee2e2', icon: 'mdi-file-cancel-outline',     label: 'Returned'   },
        ATTENDANCE_SCANNED:      { color: '#06b6d4', iconBg: '#cffafe', icon: 'mdi-qrcode-scan',             label: 'Attendance' },
      };
      return map[action] || { color: '#6b7280', iconBg: '#f3f4f6', icon: 'mdi-information-outline', label: action };
    },
    getActivityColor(action) { return this.getActivityConfig(action).color; },
    getActivityIcon(action)  { return this.getActivityConfig(action).icon; },
    getActivityBadgeStyle(action) {
      const c = this.getActivityConfig(action);
      return { background: c.iconBg, border: `1px solid ${c.color}22` };
    },
    formatActivityTime(dateStr) {
      if (!dateStr) return '';
      const diff  = Date.now() - new Date(dateStr).getTime();
      const mins  = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days  = Math.floor(diff / 86400000);
      if (mins  < 1)  return 'just now';
      if (mins  < 60) return `${mins}m ago`;
      if (hours < 24) return `${hours}h ago`;
      return `${days}d ago`;
    },
  },
};
</script>

<style scoped>
.partner-dashboard {
  min-height: 100vh;
}

/* Header */
.dash-page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}
.dash-page-sub {
  font-size: 14px;
  color: #6b7280;
}

/* Stat Cards */
.stat-card {
  border: 1px solid transparent;
  transition: box-shadow 0.2s;
  height: 100%;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}
.stat-card--green  { background: #ecfdf5; border-color: #a7f3d0; }
.stat-card--blue   { background: #eff6ff; border-color: #bfdbfe; }
.stat-card--amber  { background: #fffbeb; border-color: #fde68a; }
.stat-card--purple { background: #f5f3ff; border-color: #ddd6fe; }

.stat-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.stat-icon-box--green  { background: #a7f3d0; }
.stat-icon-box--blue   { background: #bfdbfe; }
.stat-icon-box--amber  { background: #fde68a; }
.stat-icon-box--purple { background: #ddd6fe; }

.stat-number {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1.5px;
}
.stat-number--green  { color: #059669; }
.stat-number--blue   { color: #2563eb; }
.stat-number--amber  { color: #d97706; }
.stat-number--purple { color: #7c3aed; }

.stat-label {
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
}
.stat-label--green  { color: #059669; }
.stat-label--blue   { color: #2563eb; }
.stat-label--amber  { color: #d97706; }
.stat-label--purple { color: #7c3aed; }

.stat-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 3px;
}

/* Dash Cards */
.dash-card {
  background: #ffffff;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}

/* Charts */
.chart-wrap {
  position: relative;
  width: 100%;
  height: 220px;
}
.chart-wrap--doughnut {
  height: 200px;
}

/* Legend */
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.legend-label {
  font-size: 12px;
  color: #6b7280;
}

/* View All link */
.view-all-link {
  font-size: 13px;
  color: #059669;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}
.view-all-link:hover {
  text-decoration: underline;
}

/* Intern Progress */
.intern-avatar {
  width: 34px;
  height: 34px;
  background: #f3f4f6;
  flex-shrink: 0;
}

/* Quick Actions */
.action-card {
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  height: 100%;
}
.action-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
  border-color: #d1fae5;
}
.action-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 3px;
}
.action-sub {
  font-size: 12px;
  color: #9ca3af;
}

/* Recent Activities */
.activity-item {
  transition: box-shadow 0.15s;
}
.activity-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.activity-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
}
.activity-desc {
  font-size: 12.5px;
  color: #6b7280;
  margin-bottom: 4px;
}
.activity-time {
  font-size: 12px;
  font-weight: 500;
}

.activity-view-all-link { font-size: 13px; font-weight: 600; color: #10b981; text-decoration: none; }
.activity-view-all-link:hover { text-decoration: underline; }
.activity-icon-box { display: inline-flex; }
.card-sub { font-size: 12.5px; color: #9ca3af; }
</style>
